/**
 * Busts the AG Grid datasource cache for the instrument-runs search endpoint
 * and signals the list page to purge-refresh its grid on next mount.
 *
 * Why two layers:
 *   1. React Query — `invalidateQueries` on the AG Grid cache key prefix marks
 *      every cached page/sort/filter shard stale so `getRows` re-fetches.
 *      (The datasource is configured with staleTime=60 s; without this the list
 *      would silently serve stale data for up to a minute after run creation.)
 *   2. Zustand — `setShouldRefreshInstrumentRuns(true)` triggers the list page's
 *      `useEffect` which calls `gridRef.api.refreshServerSide({ purge: true })`
 *      immediately on mount, whether or not the component was already rendered.
 *
 * Exported as a standalone function so unit tests can assert cache/store
 * side-effects without mounting the full route component.
 *
 * Assumption: `server` is the API base URL from `@/constants`.
 * Assumption: `agGridDatasourceQueryKey` returns `['ag-grid-datasource', url]`.
 */

import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { server } from '@/constants';
import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import type { QueryClient } from '@tanstack/react-query';

export async function applyInstrumentRunsListRefresh(
  orgId: string,
  queryClient: QueryClient,
  setShouldRefreshInstrumentRuns: (val: boolean) => void,
): Promise<void> {
  // Layer 1 (React Query): invalidate every AG Grid datasource cache shard.
  // Key prefix matches the full ['ag-grid-datasource', url, requestBody] entries
  // stored by createAgGridDatasource — see src/lib/createAgGridDatasource.ts.
  await queryClient.invalidateQueries({
    queryKey: agGridDatasourceQueryKey(`${server}/orgs/${orgId}/instrument_runs/search`),
  });

  // Layer 1b: belt-and-suspenders invalidation for any non-AG-Grid consumers
  // that use the plain list key (e.g. future hooks, detail pre-fetches).
  await queryClient.invalidateQueries({
    queryKey: instrumentRunQueryKeys.list(orgId),
  });

  // Layer 2 (Zustand): flag consumed by index.tsx's useEffect.
  // The list page calls gridRef.api.refreshServerSide({ purge: true }) and
  // clears the flag — see routes/instrument_runs/index.tsx.
  setShouldRefreshInstrumentRuns(true);
}
